import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Camera, Upload, Trash2, Image as ImageIcon, Loader2, Pencil, Check, X, GripVertical, Star, ChevronUp, ChevronDown } from "lucide-react";

// Every infill we display in DesignPicker. Keep in sync with FENCE_PHOTOS
// on the backend (design_catalog.py FENCE_PHOTOS keys).
const INFILL_LABELS = {
  corrugated: "Corromesh (Colorbond corrugated)",
  sheet: "Colorbond flat sheet",
  mesh: "Chain-wire mesh",
  slats: "Aluminium slats (horizontal)",
  slats_vertical: "Aluminium battens (vertical)",
  battens: "Batten screen",
  pickets: "Tubular / picket",
  spear: "Spear-top tubular",
  ringtop: "Ring-top tubular",
  curved: "Pool fence — curved top",
  paling: "Timber paling",
  rail: "Rail fence (post & rail)",
  louvres: "Louvre panel",
};

const MAX_MB = 8;

/**
 * Fence Photo Gallery Manager — staff upload real install photos per infill type.
 * Displayed as a card on the Materials page (admins/estimators only).
 */
export function FencePhotoManager() {
  const qc = useQueryClient();
  const [infill, setInfill] = useState("corrugated");
  const [caption, setCaption] = useState("");
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editCaption, setEditCaption] = useState("");

  const { data: photosByInfill = {}, isLoading } = useQuery({
    queryKey: ["design-photos"],
    queryFn: async () => (await api.get("/design-photos")).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["design-photos"] });
    qc.invalidateQueries({ queryKey: ["design-catalog"] });
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large — max ${MAX_MB} MB`);
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    if (caption.trim()) fd.append("caption", caption.trim());
    setUploading(true);
    try {
      await api.post(`/design-photos/${infill}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Photo added to ${INFILL_LABELS[infill] || infill}`);
      setCaption("");
      if (fileRef.current) fileRef.current.value = "";
      invalidate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const del = useMutation({
    mutationFn: async (pid) => (await api.delete(`/design-photos/${pid}`)).data,
    onSuccess: () => { toast.success("Photo removed"); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const patchCap = useMutation({
    mutationFn: async ({ pid, caption }) => (await api.patch(`/design-photos/${pid}`, { caption })).data,
    onSuccess: () => { toast.success("Caption updated"); setEditingId(null); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });

  const reorder = useMutation({
    mutationFn: async ({ infill, ordered_ids }) => (await api.post("/design-photos/reorder", { infill, ordered_ids })).data,
    onSuccess: () => { toast.success("Order saved — hero photo updated"); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Reorder failed"),
  });

  const list = photosByInfill[infill] || [];
  // Split into user vs stock so we only reorder USER photos.
  const userPhotos = list.filter((p) => p.source === "user");
  const stockPhotos = list.filter((p) => p.source === "stock");
  const stockCount = stockPhotos.length;
  const userCount = userPhotos.length;

  // Client-side reorder helpers. We optimistically reorder locally then POST.
  const [dragId, setDragId] = useState(null);
  const persistOrder = (nextIds) => {
    reorder.mutate({ infill, ordered_ids: nextIds });
  };
  const move = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || toIdx < 0 || toIdx >= userPhotos.length) return;
    const next = [...userPhotos];
    const [x] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, x);
    persistOrder(next.map((p) => p.id));
  };
  const onDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires setData for drag to actually start
    try { e.dataTransfer.setData("text/plain", id); } catch { /* no-op */ }
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const fromIdx = userPhotos.findIndex((p) => p.id === dragId);
    const toIdx = userPhotos.findIndex((p) => p.id === targetId);
    setDragId(null);
    if (fromIdx < 0 || toIdx < 0) return;
    move(fromIdx, toIdx);
  };

  return (
    <Card className="rounded-sm border-slate-200 industrial-shadow mb-4" data-testid="fence-photo-manager">
      <div className="p-3 sm:p-4 border-b border-slate-200 bg-slate-50/60 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start sm:items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 shrink-0 bg-cyan-100 text-cyan-700 flex items-center justify-center rounded-sm">
            <Camera size={14} />
          </div>
          <div className="min-w-0">
            <div className="font-display text-sm sm:text-base font-semibold text-slate-900">Fence photo gallery</div>
            <div className="text-[11px] sm:text-xs text-slate-500 leading-tight">Upload real CCFI install photos — they appear above the schematic in the Design Picker so clients see YOUR work, not stock images.</div>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-4 space-y-3">
        {/* Infill picker + upload row */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <div>
            <label htmlFor="infill-picker" className="stamp-label text-[10px] text-slate-500">Fence type</label>
            <Select value={infill} onValueChange={setInfill}>
              <SelectTrigger id="infill-picker" data-testid="photo-infill-picker" className="rounded-sm h-10 mt-0.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(INFILL_LABELS).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label htmlFor="photo-caption" className="stamp-label text-[10px] text-slate-500">Caption (shown to client)</label>
            <Input
              id="photo-caption"
              data-testid="photo-caption-input"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="e.g. Terrigal boundary fence — Monument"
              className="rounded-sm h-10 mt-0.5"
              maxLength={200}
            />
          </div>
          <div className="flex sm:justify-end">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
              data-testid="photo-file-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-full sm:w-auto rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="photo-upload-btn"
            >
              {uploading ? <Loader2 size={14} className="animate-spin mr-2" /> : <Upload size={14} className="mr-2" />}
              Upload photo
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
          <Badge variant="outline" className="rounded-sm text-[10px] border-cyan-300 bg-cyan-50 text-cyan-700">{userCount} uploaded</Badge>
          {stockCount > 0 && <Badge variant="outline" className="rounded-sm text-[10px] border-slate-300 bg-white text-slate-600">{stockCount} stock fallback</Badge>}
          <span>Showing gallery for <b className="text-slate-800">{INFILL_LABELS[infill] || infill}</b></span>
          {userCount > 1 && <span className="hidden sm:inline text-slate-400">· Drag tiles to reorder — top-left is the hero shown first in quotes.</span>}
          {userCount > 1 && <span className="sm:hidden text-slate-400">· Tap arrows to reorder.</span>}
        </div>

        {/* Photo grid */}
        {isLoading ? (
          <div className="text-center text-slate-400 text-sm py-6">Loading gallery…</div>
        ) : list.length === 0 ? (
          <div className="border border-dashed border-slate-300 rounded-sm p-8 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
            <ImageIcon size={22} className="text-slate-300" />
            <div>No photos yet for this fence type — upload one to replace the stock image.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2.5" data-testid="photo-grid">
            {userPhotos.map((p, idx) => {
              const editing = editingId === p.id;
              const isHero = idx === 0;
              const isDragging = dragId === p.id;
              return (
                <div
                  key={`user-${p.id}`}
                  className={`relative rounded-sm border-2 overflow-hidden bg-slate-50 transition-opacity ${isHero ? "border-amber-400 ring-2 ring-amber-200" : "border-cyan-400"} ${isDragging ? "opacity-40" : ""}`}
                  data-testid={`photo-tile-${p.id}`}
                  data-hero={isHero || undefined}
                  draggable={userPhotos.length > 1}
                  onDragStart={onDragStart(p.id)}
                  onDragOver={onDragOver}
                  onDrop={onDrop(p.id)}
                  onDragEnd={() => setDragId(null)}
                >
                  <div className="aspect-[4/3] w-full">
                    <img src={p.src} alt={p.caption || infill} className="w-full h-full object-cover" loading="lazy" draggable={false} />
                  </div>
                  {/* Top-left badges */}
                  <div className="absolute top-1 left-1 flex gap-1">
                    <Badge variant="outline" className="rounded-sm text-[9px] py-0 h-4 border-cyan-400 bg-cyan-50 text-cyan-800">YOURS</Badge>
                    {isHero && <Badge variant="outline" className="rounded-sm text-[9px] py-0 h-4 border-amber-400 bg-amber-50 text-amber-800" data-testid={`photo-hero-badge-${p.id}`}><Star size={9} className="inline mr-0.5" />HERO</Badge>}
                  </div>
                  {/* Drag handle (desktop only) */}
                  {userPhotos.length > 1 && (
                    <div className="hidden sm:flex absolute top-1 right-1 items-center gap-0.5">
                      <div className="w-6 h-6 bg-white/90 flex items-center justify-center rounded-sm text-slate-600 shadow-sm cursor-grab" title="Drag to reorder" data-testid={`photo-drag-${p.id}`}>
                        <GripVertical size={11} />
                      </div>
                    </div>
                  )}
                  {/* Actions (edit/delete + mobile reorder arrows) */}
                  <div className="absolute bottom-8 right-1 flex flex-col gap-0.5 sm:hidden">
                    {idx > 0 && (
                      <button
                        type="button"
                        onClick={() => move(idx, idx - 1)}
                        className="w-6 h-6 bg-white/90 hover:bg-white flex items-center justify-center rounded-sm text-slate-700 shadow-sm"
                        aria-label="Move up"
                        data-testid={`photo-up-${p.id}`}
                        disabled={reorder.isPending}
                      >
                        <ChevronUp size={12} />
                      </button>
                    )}
                    {idx < userPhotos.length - 1 && (
                      <button
                        type="button"
                        onClick={() => move(idx, idx + 1)}
                        className="w-6 h-6 bg-white/90 hover:bg-white flex items-center justify-center rounded-sm text-slate-700 shadow-sm"
                        aria-label="Move down"
                        data-testid={`photo-down-${p.id}`}
                        disabled={reorder.isPending}
                      >
                        <ChevronDown size={12} />
                      </button>
                    )}
                  </div>
                  <div className="absolute top-1 right-1 flex gap-0.5 sm:top-8">
                    {!editing && (
                      <>
                        <button
                          type="button"
                          onClick={() => { setEditingId(p.id); setEditCaption(p.caption || ""); }}
                          className="w-6 h-6 bg-white/90 hover:bg-white flex items-center justify-center rounded-sm text-slate-700 shadow-sm"
                          data-testid={`photo-edit-${p.id}`}
                          aria-label="Edit caption"
                        >
                          <Pencil size={11} />
                        </button>
                        <button
                          type="button"
                          onClick={() => { if (window.confirm("Delete this photo? This can't be undone.")) del.mutate(p.id); }}
                          className="w-6 h-6 bg-white/90 hover:bg-rose-50 flex items-center justify-center rounded-sm text-rose-700 shadow-sm"
                          data-testid={`photo-delete-${p.id}`}
                          aria-label="Delete photo"
                        >
                          <Trash2 size={11} />
                        </button>
                      </>
                    )}
                  </div>
                  {editing ? (
                    <div className="absolute bottom-0 left-0 right-0 bg-white/95 p-1.5 flex items-center gap-1">
                      <Input
                        autoFocus
                        value={editCaption}
                        onChange={(e) => setEditCaption(e.target.value)}
                        className="rounded-sm h-7 text-[11px] flex-1"
                        maxLength={200}
                        data-testid={`photo-edit-input-${p.id}`}
                      />
                      <button
                        type="button"
                        onClick={() => patchCap.mutate({ pid: p.id, caption: editCaption })}
                        className="w-6 h-6 bg-cyan-600 hover:bg-cyan-700 flex items-center justify-center rounded-sm text-white"
                        data-testid={`photo-save-caption-${p.id}`}
                        aria-label="Save caption"
                      >
                        <Check size={11} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="w-6 h-6 bg-slate-200 hover:bg-slate-300 flex items-center justify-center rounded-sm text-slate-700"
                        aria-label="Cancel"
                      >
                        <X size={11} />
                      </button>
                    </div>
                  ) : p.caption ? (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/55 text-white text-[10px] px-1.5 py-0.5 truncate leading-tight">
                      {p.caption}
                    </div>
                  ) : null}
                </div>
              );
            })}
            {/* Stock photos — always after user photos, not draggable */}
            {stockPhotos.map((p) => (
              <div key={`stock-${p.src}`} className="relative rounded-sm border-2 border-slate-200 overflow-hidden bg-slate-50 opacity-90" data-testid="photo-tile-stock">
                <div className="aspect-[4/3] w-full">
                  <img src={p.src} alt={p.caption || infill} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="absolute top-1 left-1">
                  <Badge variant="outline" className="rounded-sm text-[9px] py-0 h-4 border-slate-300 bg-white/90 text-slate-600">STOCK</Badge>
                </div>
                {p.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/55 text-white text-[10px] px-1.5 py-0.5 truncate leading-tight">
                    {p.caption}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export default FencePhotoManager;
